import { SectionHeader } from "~/common/components";
import { useGetPatientInteractionsActions } from "~/providers/hooks";
import { getConsultationSummaryV1Data } from "~/providers/utils";
import { PatientIntakeConsultationSummaryV1 } from "~/providers/utils/types/patientIntakePreviewTypes";
import {
  PatientScreeningConsultationSummaryV1,
  SUMMARY_TYPE,
} from "~/providers/utils/types/patientPreviewTypes";
import { PatientPreviewCurrentMedications } from "../common/PatientPreviewCurrentMedications";
import { PatientPreviewSymptomProgression } from "../common/PatientPreviewSymptomProgression";
import { PatientPreviewTextInfo } from "../common/PatientPreviewTextInfo";
import { FunctionalDomainsV1 } from "./functionalDomains";
import { PatientPreviewSelfReportedScoresV1 } from "./PatientPreviewSelfReportedScoresV1";
import { SignificantClinicalDimensions } from "./SignificantClinicalDimensions";

interface PreviewSectionsV1Props {
  screeningSummary: PatientScreeningConsultationSummaryV1;
  patientId: string;
  intakeSummary?: PatientIntakeConsultationSummaryV1;
}

export const PreviewSectionsV1 = ({
  screeningSummary,
  intakeSummary,
  patientId,
}: PreviewSectionsV1Props) => {
  const {
    screeningSummaryId,
    intakeSummaryId,
    isSummaryLiked,
    isTreatmentRecommendationsLiked,
    longSummary,
    significantClinicalDimensions,
    medications,
    scores,
    symptomProgression,
    functionalDomains,
    treatmentRecommendations,
  } = getConsultationSummaryV1Data({ screeningSummary, intakeSummary });

  const { showRateButtons, summaryType, handleRateSummary, isRatePending } =
    useGetPatientInteractionsActions({
      patientId,
      screeningSummaryId,
      intakeSummaryId,
    });

  const {
    potential_areas_of_concern: potentialAreasOfConcern,
    no_signs_of_concern: noSignsOfConcern,
  } = significantClinicalDimensions;

  return (
    <>
      <PatientPreviewTextInfo
        showRateButtons={showRateButtons}
        title="Case summary"
        info={longSummary}
        summaryType={summaryType}
        onRate={handleRateSummary}
        isLike={isSummaryLiked}
        isRatePending={isRatePending}
      />

      <div className="flex flex-col gap-4 border-b-1.5 border-b-neutral-700 pb-9">
        <SectionHeader>Current medications</SectionHeader>
        <PatientPreviewCurrentMedications medications={medications} />
      </div>

      {symptomProgression && (
        <div className="flex flex-col gap-4 border-b-1.5 border-b-neutral-700 pb-9">
          <SectionHeader>Symptom progression over time</SectionHeader>
          <PatientPreviewSymptomProgression
            symptomProgression={symptomProgression}
          />
        </div>
      )}

      {functionalDomains && (
        <FunctionalDomainsV1 functionalDomains={functionalDomains} />
      )}

      {treatmentRecommendations && (
        <PatientPreviewTextInfo
          showRateButtons={showRateButtons}
          title="Treatment recommendations"
          info={treatmentRecommendations}
          summaryType={SUMMARY_TYPE.treatment_recommendations}
          onRate={handleRateSummary}
          isLike={isTreatmentRecommendationsLiked}
          isRatePending={isRatePending}
        />
      )}

      <SignificantClinicalDimensions
        potentialAreasOfConcern={potentialAreasOfConcern}
        noSignsOfConcern={noSignsOfConcern}
      />

      <PatientPreviewSelfReportedScoresV1 scores={scores} />
    </>
  );
};
